"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { login, register } from "../lib/api";
import { useAuth } from "./AuthProvider";
import GoogleButton from "./GoogleButton";
import { Turnstile } from "./Turnstile";

type Mode = "login" | "register";

const inputClass =
  "rounded-md border border-sky-900/60 bg-ocean-800 px-3 py-2 text-sm text-foam outline-none focus:border-sky-500";

/**
 * Sleek sign-in / register overlay shown over the current page (e.g. the map).
 * Closes on success, the X button, the backdrop, or Escape — no navigation.
 */
export default function AuthModal({
  initialMode,
  onClose,
}: {
  initialMode: Mode;
  onClose: () => void;
}) {
  const { refresh } = useAuth();
  const [mode, setMode] = useState<Mode>(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [newsletterOptIn, setNewsletterOptIn] = useState(true);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Close on Escape; lock background scroll while open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  function switchMode(next: Mode) {
    setMode(next);
    setError(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (mode === "login") {
        await login(email, password);
      } else {
        if (!turnstileToken) {
          setError("Please wait for the verification check to finish.");
          return;
        }
        await register({
          email,
          username,
          password,
          displayName: displayName.trim() ? displayName.trim() : undefined,
          newsletterOptIn,
          turnstileToken,
        });
      }
      await refresh();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setBusy(false);
    }
  }

  // Render through a portal to <body> so the overlay always centers on the
  // viewport. Invokers like the submit panel use backdrop-blur, which creates a
  // containing block that would otherwise trap a `fixed` child inside the panel.
  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-ocean-950/70 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-sm rounded-2xl border border-sky-900/60 bg-ocean-900/95 p-6 shadow-2xl"
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-full text-lg text-sky-200/70 hover:bg-ocean-800 hover:text-foam"
        >
          ×
        </button>

        <img src="/logo.webp" alt="PodLog" width={224} height={80} className="mx-auto mb-4 h-10 w-auto object-contain drop-shadow-md" />

        <h2 className="mb-4 text-center text-xl font-semibold text-foam">
          {mode === "login" ? "Sign in" : "Create an account"}
        </h2>

        <GoogleButton label={mode === "login" ? "Sign in with Google" : "Sign up with Google"} />

        <div className="my-4 flex items-center gap-2 text-xs text-sky-200/50">
          <div className="h-px flex-1 bg-sky-900/60" />
          or
          <div className="h-px flex-1 bg-sky-900/60" />
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className={inputClass}
          />
          {mode === "register" && (
            <>
              <input
                type="text"
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Username"
                className={inputClass}
              />
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Display name (optional)"
                className={inputClass}
              />
            </>
          )}
          <div>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className={`${inputClass} w-full`}
            />
            {mode === "login" && (
              <div className="mt-1 text-right">
                <a
                  href="/auth/forgot-password"
                  onClick={onClose}
                  className="text-xs text-sky-300 hover:underline"
                >
                  Forgot password?
                </a>
              </div>
            )}
          </div>
          {mode === "register" && (
            <label className="flex items-center gap-2 text-sm text-foam">
              <input
                type="checkbox"
                checked={newsletterOptIn}
                onChange={(e) => setNewsletterOptIn(e.target.checked)}
                className="rounded border-sky-900/60 bg-ocean-800 text-orca focus:ring-orca focus:ring-offset-ocean-950"
              />
              Subscribe to PodLog newsletter updates
            </label>
          )}
          {/* Only mounted for register: loading the ~290 KiB Turnstile script
              on the sign-in path would cost every returning user for nothing. */}
          {mode === "register" && <Turnstile onVerify={setTurnstileToken} />}
          <button
            type="submit"
            disabled={busy || (mode === "register" && !turnstileToken)}
            className="rounded-md bg-dolphin px-3 py-2 text-sm font-semibold text-ocean-950 hover:bg-sky-300 disabled:opacity-50"
          >
            {busy
              ? mode === "login"
                ? "Signing in…"
                : "Creating account…"
              : mode === "login"
                ? "Sign in"
                : "Register"}
          </button>
          {error && <p className="text-xs text-red-400">{error}</p>}
        </form>

        <p className="mt-4 text-center text-xs text-sky-200/70">
          {mode === "login" ? (
            <>
              Need an account?{" "}
              <button onClick={() => switchMode("register")} className="text-sky-300 hover:underline">
                Register
              </button>
            </>
          ) : (
            <>
              Already have an account?{" "}
              <button onClick={() => switchMode("login")} className="text-sky-300 hover:underline">
                Sign in
              </button>
            </>
          )}
        </p>
      </div>
    </div>,
    document.body,
  );
}
